import React from 'react';

import { FormattedMessage, FormattedNumber, IntlShape } from 'react-intl';
import { useTheme } from 'styled-components';

import { statFormatter } from '@nhl/formatter-utilities';
import { BaseStyleEnums, ShortCodeEnums, ReactTableEnums } from '@nhl/nhl-breakaway-ui';
import Headshot from '@nhl/nhl-react-ui/lib/Headshot';
import headshotTypes from '@nhl/nhl-react-ui/lib/Headshot/headshotTypes';
import { createColumnHelper } from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { createGenericSortableStatsCol, createGenericStatsCol, leftPaddedColumn } from '~/core/commonTableColumns';
import { Alignments, PlayerStatAbbrev, PlayerStatNames } from '~/core/enums';
import { getPlayerHref, parseValueFromLocalizedAttribute } from '~/core/utils';
import { addSortableHeaderTitle } from '~/views/components/reactTable/utils';
import { Player } from '~/views/pages/dotcom/player/types';

import { PlayerColumn, StyledPlayerLink } from './columnStyles';

const { playerInfoDescriptions, statsDescriptions } = CommonMessages;

const columnHelper = createColumnHelper<Player>();
export const createPlayerCol = createGenericStatsCol(columnHelper);
export const createSortablePlayerCol = createGenericSortableStatsCol(columnHelper);

export const HEADSHOT_NAME_COL = (
    intl: IntlShape,
    screenSize: number,
    isWebview: boolean,
    isGoalie = false,
    teamTriCode?: ShortCodeEnums.TeamTriCode
) => ({
    ...createSortablePlayerCol(intl, {
        acc: 'lastName',
        header: playerInfoDescriptions.long.player,
        alignHeader: Alignments.left,
    }),
    cell: function CellComponent({ row }) {
        const theme = useTheme();
        const { firstName: unparsedFirstName, headshot, lastName: unparsedLastName, id, playerId: pId } = row.original;
        const firstName = parseValueFromLocalizedAttribute(unparsedFirstName);
        const lastName = parseValueFromLocalizedAttribute(unparsedLastName);
        const playerId = id || pId;

        return (
            <PlayerColumn aria-label={`${firstName} ${lastName}`}>
                {headshot !== null ? (
                    <Headshot
                        type={isGoalie ? headshotTypes.GOALIE : headshotTypes.SKATER}
                        urlOverride={headshot}
                        size={50}
                        backgroundColor={teamTriCode ? theme.Team[teamTriCode]?.gamecenter : theme.Surface.secondary}
                    />
                ) : null}
                {playerId !== null && (
                    <StyledPlayerLink href={getPlayerHref(playerId, isWebview)} rel="noreferrer">
                        {firstName} {lastName}
                    </StyledPlayerLink>
                )}
            </PlayerColumn>
        );
    },
    size: screenSize <= BaseStyleEnums.ScreenWidthBreakpoints.small ? 170 : 220,
    sortingFn: (a, b) => {
        const aName = `${a.original.lastName.default} ${a.original.firstName.default}`;
        const bName = `${b.original.lastName.default} ${b.original.firstName.default}`;
        return bName.toLowerCase().localeCompare(aName.toLowerCase());
    },
    meta: {
        ...leftPaddedColumn(screenSize >= BaseStyleEnums.ScreenWidthBreakpoints.medium ? 16 : 8),
        ...addSortableHeaderTitle(CommonMessages.playerInfoDescriptions.long.player, intl),
        scope: ReactTableEnums.Scopes.ROW,
    },
});

export const GAMES_PLAYED_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'gamesPlayed',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.gamesPlayed],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.gamesPlayed], intl),
    });

export const TIME_ON_ICE_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'timeOnIce',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.timeOnIce],
        size: 80,
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.timeOnIce], intl),
    });

// SKATER COLUMNS
export const GOALS_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'goals',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.goals],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.goals], intl),
    });

export const ASSISTS_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'assists',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.assists],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.assists], intl),
    });

export const POINTS_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'points',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.points],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.points], intl),
    });

export const PLUS_MINUS_COL = (intl: IntlShape) => ({
    ...createSortablePlayerCol(intl, {
        acc: 'plusMinus',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.plusMinus],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.plusMinus], intl),
    }),
    cell: (cell) =>
        cell.getValue() === undefined ? (
            <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />
        ) : (
            <FormattedNumber value={cell.getValue()} signDisplay="exceptZero" />
        ),
});

export const PIM_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'pim',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.penaltyMinutes],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.penaltyMinutes], intl),
    });

export const PENALTY_MINS_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'penaltyMins',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.penaltyMinutes],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.penaltyMinutes], intl),
    });

export const PPG_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'powerPlayGoals',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.powerPlayGoals],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.powerPlayGoals], intl),
    });

export const PPP_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'powerPlayPoints',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.powerPlayPoints],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.powerPlayPoints], intl),
    });

export const SHOTS_COL = (intl: IntlShape, size?: number) =>
    createSortablePlayerCol(intl, {
        acc: 'shots',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.shots],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.shotsOnGoal], intl),
        size,
    });

export const SHG_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'shorthandedGoals',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.shortHandedGoals],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.shortHandedGoals], intl),
    });

export const SHP_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'shorthandedPoints',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.shortHandedPoints],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.shortHandedPoints], intl),
    });

// GOALIE COLUMNS
export const WINS_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'wins',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.wins],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.wins], intl),
    });

export const LOSSES_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'losses',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.losses],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.losses], intl),
    });

export const TIES_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'ties',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.ties],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.ties], intl),
    });

export const OT_LOSSES_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'otLosses',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.overtimeLosses],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.overtimeLosses], intl),
    });

export const SAVE_PCTG_COL = (intl: IntlShape) => ({
    ...createSortablePlayerCol(intl, {
        acc: 'savePctg',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.savePctg],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.savePctg], intl),
    }),
    cell: (cell) =>
        cell?.getValue() !== undefined ? (
            statFormatter.displayValueAs('savePctg')(cell?.getValue())
        ) : (
            <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />
        ),
});

export const GOALS_AGAINST_COL = (intl: IntlShape) => ({
    ...createSortablePlayerCol(intl, {
        acc: 'goalsAgainst',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.goalsAgainst],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.goalsAgainst], intl),
    }),
    cell: (cell) => statFormatter.displayValueAs('goalsAgainst')(cell?.getValue()),
});

export const GOALS_AGAINST_AVERAGE_COL = (intl: IntlShape) => ({
    ...createSortablePlayerCol(intl, {
        acc: 'goalsAgainstAvg',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.goalsAgainstAvg],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.goalsAgainstAvg], intl),
    }),
    cell: ({ cell, row }) =>
        statFormatter.displayValueAs('goalsAgainstAverage')(cell?.getValue(), row.original.gamesPlayed >= 1),
});

export const SHUTOUTS_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'shutouts',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.shutouts],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.shutouts], intl),
    });
